import React from 'react';
import { Descriptions, Divider, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Asset, Company, AssetCategory, Location, Employee } from '../../types';
import { formatVND, formatDate, mapStatusToVN, mapTechnicalConditionToVN } from '../../utils/formatters';
import BarcodeBase64 from '../BarcodeBase64';

const { Title } = Typography;

interface AssetInfoTabProps {
  asset: Asset | null;
  companies: Company[];
  categories: AssetCategory[];
  locations: Location[];
  employees: Employee[];
  suppliers: any[];
  qrCodeBase64?: string | null;
}

const AssetInfoTab: React.FC<AssetInfoTabProps> = ({
  asset,
  companies,
  categories,
  locations,
  employees,
  suppliers,
  qrCodeBase64,
}) => {
  if (!asset) return null;

  const companyName = companies.find((c) => c.id === asset.company_id)?.name || asset.company_id || '—';
  const categoryName = categories.find((c) => c.id === asset.category_id)?.name || asset.category_id || '—';
  const locationName = locations.find((l) => l.id === asset.location_id)?.name || asset.location_id || '—';
  const employeeName = employees.find((e) => e.id === asset.assigned_to_employee_id)?.full_name || asset.assigned_to_employee_id || '—';
  const supplierName = (suppliers as any[]).find((s: any) => s.id === asset.supplier_id)?.name || asset.supplier_id || '—';

  let specs = asset.technical_specifications;
  if (typeof specs === 'string') {
    try {
      specs = JSON.parse(specs);
    } catch {
      specs = {};
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>
        <InfoCircleOutlined style={{ marginRight: 8 }} />
        Thông tin chung
      </Title>
      {/* QR CODE + BARCODE + TÊN/MÃ TÀI SẢN */}
      <div
        style={{
          background: '#f0f5ff',
          border: '1.5px solid #d9d9d9',
          borderRadius: 10,
          padding: 20,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 32,
          boxShadow: '0 2px 8px #e6f7ff'
        }}
      >
        {qrCodeBase64 && (
          <img
            src={qrCodeBase64}
            alt="QR code"
            width={120}
            height={120}
            style={{
              background: '#fff',
              border: 'none',
              boxShadow: '0 2px 8px #e6f7ff'
            }}
          />
        )}
        <div>
          <div style={{ fontWeight: 700, fontSize: 22, color: '#222' }}>{asset?.name}</div>
          <div style={{ color: '#1890ff', fontSize: 18, fontWeight: 600 }}>{asset?.asset_code}</div>
          <div style={{ marginTop: 12 }}>
            <BarcodeBase64 
              value={asset?.asset_code || ''} 
              height={48} 
              displayValue={false}
              margin={0}
              style={{ margin: 0, padding: 0 }}
            />
          </div>
        </div>
      </div>
      <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Công ty">{companyName}</Descriptions.Item>
        <Descriptions.Item label="Mã tài sản">{asset?.asset_code}</Descriptions.Item>
        <Descriptions.Item label="Tên tài sản">{asset?.name}</Descriptions.Item>
        <Descriptions.Item label="Danh mục">{categoryName}</Descriptions.Item>
        <Descriptions.Item label="Vị trí">{locationName}</Descriptions.Item>
        <Descriptions.Item label="Nhân viên được giao">{employeeName}</Descriptions.Item>
        <Descriptions.Item label="Tình trạng">{mapStatusToVN(asset?.status || '')}</Descriptions.Item>
        <Descriptions.Item label="Tình trạng kỹ thuật">{mapTechnicalConditionToVN(asset?.technical_condition || '')}</Descriptions.Item>
        <Descriptions.Item label="Tài sản cha">{asset?.parent_asset_id || '—'}</Descriptions.Item>
        <Descriptions.Item label="Mô tả">
          {asset?.description ? (
            <div style={{ whiteSpace: 'pre-line' }}>{asset.description}</div>
          ) : '—'}
        </Descriptions.Item>
      </Descriptions>
      <Divider style={{ margin: '16px 0' }} />
      <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>
        Thông số kỹ thuật
      </Title>
      <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
        {specs && typeof specs === 'object' && Object.keys(specs).length > 0 ? (
          Object.entries(specs).map(([key, value]) => (
            <Descriptions.Item key={key} label={key}>
              {value === undefined || value === null || value === ''
                ? '—'
                : (typeof value === 'object'
                    ? (Array.isArray(value)
                        ? (value.length > 0 ? JSON.stringify(value) : '—')
                        : (Object.keys(value).length > 0 ? JSON.stringify(value) : '—')
                      )
                    : key.toLowerCase().includes('mô tả') 
                      ? <div style={{ whiteSpace: 'pre-line' }}>{String(value)}</div>
                      : String(value)
                  )
              }
            </Descriptions.Item>
          ))
        ) : (
          <Descriptions.Item label="Thông số kỹ thuật">—</Descriptions.Item>
        )}
      </Descriptions>
      <Divider style={{ margin: '16px 0' }} />
      <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>
        Thông tin mua sắm
      </Title>
      <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Giá trị">{formatVND(asset?.purchase_cost)}</Descriptions.Item>
        <Descriptions.Item label="Ngày mua">{formatDate(asset?.purchase_date)}</Descriptions.Item>
        <Descriptions.Item label="Nhà cung cấp">{supplierName}</Descriptions.Item>
      </Descriptions>
      <Divider style={{ margin: '16px 0' }} />
      <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>
        Bảo hành & kiểm kê
      </Title>
      <Descriptions column={2} size="small">
        <Descriptions.Item label="Ngày hết hạn bảo hành">{formatDate(asset?.warranty_expiry_date)}</Descriptions.Item>
        <Descriptions.Item label="Ngày kiểm kê gần nhất">{formatDate(asset?.last_audit_date)}</Descriptions.Item>
      </Descriptions>
    </div>
  );
};

export default AssetInfoTab; 